import React, { useState, useEffect } from 'react';
import {
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  useMediaQuery,
  useTheme,
  Stack
} from '@mui/material';
import { Close } from '@mui/icons-material';
import InputMask from 'react-input-mask';
import SystemAccessSelector from './SystemAccessSelector';
import BWITextField from '../shared/BWITextField';
import { Account, User } from '../../state/types';
import { useAccountContext } from '../../context/AccountContext';
import RoleBasedAccess, { userHasAccess } from '../../services/RoleBasedAccess';
import { useSession } from '../../context/SessionContext';

interface AccountModalProps {
  open: boolean;
  onClose: () => void;
  account?: Account | null;
}

const roles = ["Admin", "Support", "Billing", "Auditor", "Client", "Client Plus", "Syndication Partner"];
const internalRoles = ["Admin", "Support", "Billing", "Auditor"];

const AccountModal: React.FC<AccountModalProps> = ({ open, onClose, account }) => {
  const { addAccount, updateAccount, deleteAccount, resendTempPassword } = useAccountContext();
  const { user, startMasquerade, systems } = useSession(); 
  if (!user) return null;
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState('');
  const [selectedSystems, setSelectedSystems] = useState<string[]>([]);
  const [resending, setResending] = useState(false);
  const isInternalUser = internalRoles.includes(role);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (account) {
      setEmail(account.email);
      setPhoneNumber(account.phone_number?.startsWith("+1") ? account.phone_number.slice(2) : account.phone_number || '');
      setFirstName(account.first_name || '');
      setLastName(account.last_name || '');
      setRole(account.role || '');
      setSelectedSystems(account.system_access || []);
    } else {
      setEmail('');
      setPhoneNumber('');
      setFirstName('');
      setLastName('');
      setRole('');
      setSelectedSystems([]);
    }
  }, [account]);

  useEffect(() => {
    if (!open) {
      setEmail('');
      setPhoneNumber('');
      setFirstName('');
      setLastName('');
      setRole('');
      setSelectedSystems([]);
    }
  }, [open]);

  const formatPhoneNumberToE164 = (phone: string): string => {
    const digits = phone.replace(/\D/g, '');
    return digits.length === 10 ? `+1${digits}` : `+${digits}`;
  };

  const handleSystemSelection = (systemCode: string) => {
    setSelectedSystems((prev) =>
      prev.includes(systemCode) ? prev.filter((s) => s !== systemCode) : [...prev, systemCode]
    );
  };

  const handleAddOrUpdateAccount = async () => {
    if (!email) {
      alert('Please fill out email');
      return;
    }
    if (!firstName) {
      alert('Please fill out first name');
      return;
    }
    if (!lastName) {
      alert('Please fill out last name');
      return;
    }
    if (!phoneNumber) {
      alert('Please fill out phone number');
      return;
    }
    const newAccountData = {
      email,
      first_name: firstName,
      last_name: lastName,
      phone_number: formatPhoneNumberToE164(phoneNumber),
      role,
      system_access: selectedSystems
    };

    if (!account) {
      await addAccount(newAccountData);
    } else {
      await updateAccount({
        ...account,
        ...newAccountData
      }, user);
    }

    onClose()
  };

  const handleDeleteAccount = async () => {
    if (!account) return;

    await deleteAccount(account.username)
    onClose();
  };

  const handleResendPassword = async () => {
    if (!account?.username) return;

    const userConfirmed = window.confirm(
      "Warning - this will permanently delete and recreate the user. This process could fail and require manual support. Do you wish to continue?"
    );

    if (!userConfirmed) {
      return;
    }

    setResending(true);
    try {
      await resendTempPassword(account.username, account.email);
      onClose();
    } catch (error) {
      console.error("Failed to resend temporary password:", error);
      alert("Failed to resend temporary password. Please check the console for details.");
    } finally {
      setResending(false);
    }
  };

  const handleStartMasquerade = () => {
    if (!account) return;

    startMasquerade(account as User); 
    onClose();
  };

  const canMasquerade =
    !!account &&
    (user.role === "Admin" || user.role === "Support") &&
    user.username !== account.username &&
    !["Admin"].includes(account.role);

  const showSystemSelector = role !== '' && !internalRoles.includes(role);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={showSystemSelector ? "md" : "sm"}
      fullScreen={isMobile}
    >
      {open && (<>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {account ? 'Edit Account' : 'Add Account'}
          <IconButton onClick={onClose} sx={{ marginLeft: 'auto' }} data-cy="account-modal-close-btn">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ pt: 1 }}>
            {/* Left Column - User Info */}
              <Grid item xs={12} md={showSystemSelector ? 6 : 12}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <BWITextField
                  label="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  size={isMobile ? 'small' : 'medium'}
                  fullWidth
                  data-cy="account-first-name-input" />
                <BWITextField
                  label="Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  size={isMobile ? 'small' : 'medium'}
                  fullWidth
                  data-cy="account-last-name-input" />
                <BWITextField
                  label="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value.toLocaleLowerCase())}
                  size={isMobile ? 'small' : 'medium'}
                  fullWidth
                  data-cy="account-email-input" />
                <InputMask mask="(999) 999-9999"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}>
                  {() => <BWITextField label="Phone Number" fullWidth
                    size={isMobile ? 'small' : 'medium'} data-cy="account-phone-input" />}
                </InputMask>
                <RoleBasedAccess allowedRoles={["Admin", "Support"]}>
                  <FormControl fullWidth
                    size={isMobile ? 'small' : 'medium'} >
                    <InputLabel>
                      Role
                    </InputLabel>
                    <Select
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                      label="Role"
                      disabled={user?.role !== "Admin" && account?.role === "Admin"}
                      data-cy="account-role-select"
                    >
                      {roles.filter((r) => {
                        if (user?.role !== "Admin" && account?.role === "Admin") return true
                        if (r === "Admin") return user?.role === "Admin";
                        return true;
                      }).map((r) => (
                        <MenuItem key={r} value={r}>
                          {r}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </RoleBasedAccess>
              </Box>
            </Grid>

            {/* Right Column - System Access */}
            {showSystemSelector && (
              <RoleBasedAccess allowedRoles={["Admin", "Support"]}>
                <Grid item xs={12} md={6}>
                  <SystemAccessSelector
                    selectedSystems={selectedSystems}
                    onSystemSelect={handleSystemSelection}
                    disabled={isInternalUser}
                  />
                </Grid>
              </RoleBasedAccess>
            )}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: isMobile ? 2 : 3, flexDirection: 'column' }}>
          {account ? (
            <Stack spacing={1.5} width="100%">
              {/* Primary action is always visible */}
              <Button fullWidth variant="contained" color="warning" onClick={handleAddOrUpdateAccount} data-cy="account-update-btn">
                Update
              </Button>

              {canMasquerade && (
                <RoleBasedAccess allowedRoles={["Admin", "Support"]}>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="info" // Use a distinct color
                    onClick={handleStartMasquerade}
                    data-cy="account-masquerade-btn"
                  >
                    Act as this User
                  </Button>
                </RoleBasedAccess>
              )}

              {/* Secondary/Danger zone actions */}
              <RoleBasedAccess allowedRoles={["Admin", "Support"]}>
                {((user?.role === "Admin") || ((user?.role === "Support") && (account?.role !== "Admin"))) && (
                  <>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="secondary"
                      disabled={resending}
                      onClick={handleResendPassword}
                      data-cy="account-resend-password-btn"
                    >
                      {resending ? "Sending..." : "Resend Temp Password"}
                    </Button>

                    <Button
                      fullWidth
                      variant="outlined"
                      color="error"
                      onClick={handleDeleteAccount}
                      data-cy="account-delete-btn"
                    >
                      Delete
                    </Button>
                  </>
                )}
              </RoleBasedAccess>
            </Stack>
          ) : (
            <Button
              fullWidth
              variant="contained"
              onClick={handleAddOrUpdateAccount}
              data-cy="account-signup-btn"
              sx={{
                backgroundColor: (theme) => theme.palette.status.accent,
                color: '#fff',
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.status.accent,
                  opacity: 0.9,
                },
              }}
            >
              Sign Up
            </Button>
          )}
        </DialogActions>
      </>)}
    </Dialog>
  );
};

export default AccountModal;
